import {
    DragInteraction,
    GenericInteraction,
    KeyboardInteraction,
    MouseInteraction,
    TouchInteraction
} from "./interactions";

class StudyAlignLib {

    apiVersion: string;
    url: string;
    studyId: number;
    apiUrl: string;
    loggerKey: string;
    sse: EventSource;

    // Interaction Lists (Web Events only), needed for bulk saving
    mouseInteractionList: MouseInteraction[] = [];
    dragInteractionList: DragInteraction[] = [];
    keyboardInteractionList: KeyboardInteraction[] = [];
    touchInteractionList: TouchInteraction[] = [];
    genericInteractionList: GenericInteraction[] = [];

    constructor(url = "http://localhost:8080", studyId: number) {
        this.apiVersion = "v1";
        this.url = url;
        this.studyId = studyId;
        this.apiUrl = this.url + "/api/" + this.apiVersion;
    }

    static getParamsFromURL = () => {
        const url = new URL(window.location.href);
        const studyId = url.searchParams.get("study_id");
        const conditionId = url.searchParams.get("condition_id") || 1; // value from get parameter or 1 (default)
        const loggerKey = url.searchParams.get("logger_key"); // needed for logging
        const participantToken = url.searchParams.get("participant_token");

        return {
            studyId: studyId,
            conditionId: conditionId,
            loggerKey: loggerKey,
            participantToken: participantToken
        }
    }

    getTimestamp() {
        return Date.now;
    }

    getTimestampWithOffset() {
        const date = new Date();
        date.setMinutes(date.getMinutes() + (-1 * date.getTimezoneOffset()))
        return date.getTime();
    }

    setHeaders(options: Options, refresh: boolean = false, skipContentType: boolean = false) {
        const access_token = !refresh ? this.readTokens("access_token") : this.readTokens("refresh_token");
        options.headers["Authorization"] = "Bearer " + access_token;
        // For multipart uploads the browser must set Content-type (incl. the
        // boundary) itself, so callers can opt out of the JSON default.
        if (!skipContentType) {
            options.headers["Content-type"] = "application/json";
        }
    }

    setLoggerHeaders(options: Options) {
        if (this.loggerKey) {
            options.headers["Studyalign-Logger-Key"] = this.loggerKey;
        }
        options.headers["Content-type"] = "application/json";
    }

    request(options: Options) {
        const encodeParams = (data) => {
            return Object.keys(data)
                // Skip null/undefined values so they are not sent as the literal
                // strings "null"/"undefined" (e.g. an omitted search filter).
                .filter((key) => data[key] !== null && data[key] !== undefined)
                .map((key) => {
                    return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
                }).join('&');
        }
        return new Promise( (resolve, reject) => {
            let url = this.apiUrl + "/" + options.path;
            let xhr = new XMLHttpRequest();
            if (options.asQuery) {
                let params = options.params;
                let encodedParams = "";
                if (params && typeof params === 'object') {
                    encodedParams = encodeParams(params);
                }
                url = url + "?" + encodedParams;
            }
            xhr.open(options.method, url);
            // For Exports
            if (options.responseType) {
                xhr.responseType = options.responseType;
            }
            if (options.onload) {
                xhr.onload = options.onload;
            }
            else {
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        let body;
                        if (options.responseType === "blob") {
                            body = xhr.response;
                        } else {
                            body = xhr.response ? JSON.parse(xhr.response) : "";
                        }
                        resolve({
                                status: xhr.status,
                                body: body
                            });
                    } else {
                        reject({
                            status: xhr.status,
                            statusText: xhr.statusText,
                            requestBody: options.body
                        });
                    }
                };
            }
            if (options.onprogress) {
                xhr.onprogress = options.onprogress;
            }
            if (options.onerror) {
                xhr.onerror = options.onerror;
            } else {
                xhr.onerror = () => {
                    reject({
                        status: xhr.status,
                        statusText: xhr.statusText,
                        requestBody: options.body
                    });
                };
            }

            if (options.headers) {
                Object.keys(options.headers).forEach((key) => {
                    xhr.setRequestHeader(key, options.headers[key]);
                });
            }

            if (options.method === "GET" || options.method === "DELETE") {
                let params = options.params;
                let encodedParams = "";
                if (params && typeof params === 'object') {
                    encodedParams = encodeParams(params)
                }
                xhr.send(encodedParams);
            }
            if (options.method === "POST" || options.method === "PATCH" || options.method === "PUT") {
                if (options.asQuery) {
                    xhr.send()
                } else if (options.multipart) {
                    xhr.send(options.body as any)
                } else {
                    xhr.send(options.formData ? encodeParams(options.body) : JSON.stringify(options.body))
                }
            }
        });
    }

    basicCreate(path: string, data: object) {
        let options: Options = {
            method: "POST",
            path: path,
            headers: {}
        }
        this.setHeaders(options);
        if (data) {
            options.body = data;
        }
        return this.request(options);
    }

    basicRead(path: string) {
        const options: Options = {
            method: "GET",
            path: path,
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    basicUpdate(path: string, data: object) {
        let options: Options = {
            method: "PATCH",
            path: path,
            headers: {}
        }
        this.setHeaders(options);
        options.body = data;
        return this.request(options);
    }

    basicDelete(path: string) {
        let options: Options = {
            method: "DELETE",
            path: path,
            headers: {}
        }
        this.setHeaders(options);
        const yo = this.request(options);
        console.log(yo)
        return yo
    }

    // Admin related functions
    userLogin(username: string, password: string) {
        const options: Options = {
            method: "POST",
            path: "users/login",
            headers: {},
            body: {username: username, password: password},
            formData: true
        };
        options.headers["Content-type"] = "application/x-www-form-urlencoded";
        return this.request(options);
    }

    userMe(): Promise<any> {
        return this.basicRead("users/me");
    }

    userRefreshToken() {
        const options: Options = {
            method: "GET",
            path: "users/refresh",
            headers: {}
        }
        this.setHeaders(options, true);
        return this.request(options);
    }

    getUsersCount() {
        return this.basicRead("users/count");
    }

    getUsers(offset: number = 0, limit: number = 100, orderBy: string = "id", direction: string = "asc", search: string = null) {
        const options: Options = {
            method: "GET",
            path: "users",
            headers: {},
            params: { offset: offset, limit: limit, order_by: orderBy, direction: direction, search: search},
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    getUser(userId: number) {
        return this.basicRead("users/" + userId);
    }

    createUser(user: object) {
        return this.basicCreate("users", user)
    }

    updateUser(userId: number, user: object) {
        return this.basicUpdate("users/" + userId, user);
    }

    deleteUser(userId: number) {
        return this.basicDelete("users/" + userId);
    }

    getRoles() {
        return this.basicRead("users/roles");
    }

    // ---- Registration & password recovery (unauthenticated) ---- //

    registerUser(user: object) {
        return this.basicCreate("users/register", user);
    }

    activateUser(token: string) {
        return this.basicRead("users/" + token + "/activate");
    }

    requestPasswordReset(email: string) {
        return this.basicCreate("users/forgot-password", { email: email });
    }

    resetPassword(token: string, password: string) {
        return this.basicCreate("users/reset-password", { token: token, password: password });
    }

    // ---- MAINLY FOR USE IN ADMIN FRONTEND ---- //

    // Studies

    getStudies() {
        return this.basicRead("studies");
    }

    createStudy(study: object) {
        return this.basicCreate("studies", study);
    }

    updateStudy(studyId: number, study: object) {
        return this.basicUpdate("studies/" + studyId, study);
    }

    deleteStudy(studyId: number) {
        return this.basicDelete("studies/" + studyId);
    }

    generateProcedures(studyId: number) {
        const options: Options = {
            method: "POST",
            path: "studies/" + studyId + "/procedures",
            headers: {},
            body: {},
            formData: true,
        }
        this.setHeaders(options);
        return this.request(options);
    }

    getProcedureConfigMain(studyId: number) {
        return this.basicRead("studies/" + studyId + "/procedure-config");
    }

    getParticipants(studyId: number, offset: number = 0, limit: number = 100, orderBy: string = "id", direction: string = "asc") {
        const options: Options = {
            method: "GET",
            path: "studies/" + studyId + "/participants",
            headers: {},
            params: { offset: offset, limit: limit, order_by: orderBy, direction: direction },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    getParticipantsCount(studyId: number, participant_state: string, paused: string, proceed: string) {
        const params = { participant_state, paused, proceed };
        // Remove undefined parameters
        Object.keys(params).forEach(key => {
            if (params[key] === undefined) {
                delete params[key];
            }
        });
        const options: Options = {
            method: "GET",
            path: "studies/" + studyId + "/participants/count",
            headers: {},
            params: params,
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    generateParticipants(studyId: number, amount: number) {
        const options: Options = {
            method: "POST",
            path: "studies/" + studyId + "/participants",
            headers: {},
            params: {amount: amount},
            asQuery: true
        }
        this.setHeaders(options);
        return this.request(options);
    }

    addParticipants(studyId: number, amount: number) {
        const options: Options = {
            method: "POST",
            path: "studies/" + studyId + "/add-participants",
            headers: {},
            params: {amount: amount},
            asQuery: true
        }
        this.setHeaders(options);
        return this.request(options);
    }

    exportStudySchema(studyId: number) {
        return this.basicRead("studies/" + studyId + "/export");
    }

    importStudySchema(studySchema: object) {
        return this.basicCreate("studies/import", studySchema);
    }

    duplicateStudy(studyId: number) {
        return this.basicRead("studies/" + studyId + "/duplicate");
    }

    // Procedure Configs

    getProcedureConfig(procedureConfigId: number) {
        return this.basicRead("procedure-configs/" + procedureConfigId);
    }

    getProcedureConfigOverview(procedureConfigId) {
        return this.basicRead("procedure-configs/" + procedureConfigId + "/overview");
    }

    createProcedureConfigBlock(block: object) {
        return this.basicCreate("procedure-configs/block", block);
    }

    deleteProcedureConfigBlock(blockId: number) {
        return this.basicDelete("procedure-configs/block/" + blockId);
    }

    createSingleProcedureConfigStep(procedureConfigId: number, procedureConfigStep: object) {
        const options: Options = {
            method: "POST",
            path: "procedure-configs/" + procedureConfigId + "/step",
            headers: {},
            body: procedureConfigStep
        }
        this.setHeaders(options);
        return this.request(options);
    }

    createProcedureConfigSteps(procedureConfigId: number, procedureConfigSteps: object) {
        const options: Options = {
            method: "POST",
            path: "procedure-configs/" + procedureConfigId + "/create-steps",
            headers: {},
            body: procedureConfigSteps
        }
        this.setHeaders(options);
        return this.request(options);
    }

    updateProcedureConfig(procedureConfigId: number, procedureConfigSteps: object) {
        return this.basicUpdate("procedure-configs/" + procedureConfigId, procedureConfigSteps);
    }

    // Conditions

    getConditionIds(studyId: number) {
        const options: Options = {
            method: "GET",
            path: "conditions/ids",
            headers: {},
            body: {study_id: studyId},
            formData: true,
        }
        this.setHeaders(options);
        return this.request(options);
    }

    getCondition(conditionId: number) {
        return this.basicRead("conditions/" + conditionId);
    }

    getConditions(studyId: number) {
        return this.basicRead("studies/" + studyId + "/conditions");
    }

    createCondition(condition: object) {
        return this.basicCreate("conditions", condition);
    }

    updateCondition(conditionId: number, condition: object) {
        return this.basicUpdate("conditions/" + conditionId, condition);
    }

    deleteCondition(conditionId: number) {
        return this.basicDelete("conditions/" + conditionId);
    }

    getTasks(studyId: number) {
        return this.basicRead("studies/" + studyId + "/tasks");
    }

    getTexts(studyId: number) {
        return this.basicRead("studies/" + studyId + "/texts");
    }

    getQuestionnaires(studyId: number) {
        return this.basicRead("studies/" + studyId + "/questionnaires");
    }

    getPauses(studyId: number) {
        return this.basicRead("studies/" + studyId + "/pauses");
    }

    // Collaborators

    getCollaborators(studyId: number) {
        return this.basicRead("collaborators/study/" + studyId);
    }

    createCollaborator(collaborator: object) {
        return this.basicCreate("collaborators", collaborator);
    }

    deleteCollaborator(collaboratorId: number) {
        return this.basicDelete("collaborators/" + collaboratorId);
    }

    // Groups

    getGroups() {
        return this.basicRead("groups");
    }

    createGroup(group: object) {
        return this.basicCreate("groups", group);
    }

    getGroup(groupId: number) {
        return this.basicRead("groups/" + groupId);
    }

    updateGroup(groupId: number, group: object) {
        return this.basicUpdate("groups/" + groupId, group);
    }

    deleteGroup(groupId: number) {
        return this.basicDelete("groups/" + groupId);
    }

    // Group Members

    getGroupMembers(groupId: number) {
        return this.basicRead("groups/" + groupId + "/members");
    }

    deleteGroupMember(groupId: number, userId: number) {
        return this.basicDelete("groups/" + groupId + "/members/" + userId);
    }

    // Group Invites

    getGroupInvites(groupId: number) {
        return this.basicRead("groups/" + groupId + "/invites");
    }

    createGroupInvite(groupId: number, group_invite: object) {
        return this.basicCreate("groups/" + groupId + "/invites", group_invite);
    }

    acceptGroupInvite(token: string) {
        return this.basicRead("groups/invite/" + token + "/accept");
    }

    resendGroupInvite(token: string) {
        return this.basicRead("groups/invite/" + token + "/resend");
    }

    deleteGroupInvite(token: string) {
        return this.basicDelete("groups/invite/" + token);
    }

    // Group Logo

    createGroupLogo(groupId: number, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        const options: Options = {
            method: "POST",
            path: "groups/" + groupId + "/logo",
            headers: {},
            body: formData,
            multipart: true,
        }
        // Skip the JSON Content-type so the browser sets multipart/form-data
        // with the correct boundary itself.
        this.setHeaders(options, false, true);
        return this.request(options);
    }

    getGroupLogo(groupId: number) {
        return this.basicRead("groups/" + groupId + "/logo");
    }

    deleteGroupLogo(groupId: string) {
        return this.basicDelete("groups/" + groupId + "/logo");
    }


    // Procedures

    getProcedures(studyId: number) {
        const options: Options = {
            method: "GET",
            path: "procedures",
            headers: {},
            body: {study_id: studyId},
            formData: true,
        }
        this.setHeaders(options);
        return this.request(options);
    }

    procedureStepPush(participantToken: string) {
        return this.basicRead("procedures/push/" + participantToken);
    }

    // Participants

    getParticipantsByProcedure(procedureId: number) {
        const options: Options = {
            method: "GET",
            path: "participants",
            headers: {},
            body: {procedure_id: procedureId},
            formData: true,
        }
        this.setHeaders(options);
        return this.request(options);
    }

    getParticipantById(participantId: number) {
        return this.basicRead("participants/" + participantId);
    }

    endParticipantPause(participantToken: string) {
        return this.basicRead("participants/" + participantToken + "/end-pause");
    }

    //Tasks

    createTask(task: object) {
        return this.basicCreate("tasks", task);
    }

    getTask(taskId: number) {
        return this.basicRead("tasks/" + taskId);
    }

    updateTask(taskId: number, task: object) {
        return this.basicUpdate("tasks/" + taskId, task);
    }

    deleteTask(taskId: number) {
        return this.basicDelete("tasks/" + taskId);
    }

    //Texts

    createText(text: object) {
        return this.basicCreate("texts", text);
    }

    getText(textId: number) {
        return this.basicRead("texts/" + textId);
    }

    updateText(textId: number, text: object) {
        return this.basicUpdate("texts/" + textId, text);
    }

    deleteText(textId: number) {
        return this.basicDelete("texts/" + textId);
    }

    //Questionnaires

    createQuestionnaire(questionnaire: object) {
        return this.basicCreate("questionnaires", questionnaire);
    }

    getQuestionnaire(questionnaireId: number) {
        return this.basicRead("questionnaires/" + questionnaireId);
    }

    updateQuestionnaire(questionnaireId: number, questionnaire: object) {
        return this.basicUpdate("questionnaires/" + questionnaireId, questionnaire);
    }

    deleteQuestionnaire(questionnaireId: number) {
        return this.basicDelete("questionnaires/" + questionnaireId);
    }

    //Pauses

    createPause(pause: object) {
        return this.basicCreate("pauses", pause);
    }

    getPause(pauseId: number) {
        return this.basicRead("pauses/" + pauseId);
    }

    updatePause(pauseId: number, pause: object) {
        return this.basicUpdate("pauses/" + pauseId, pause);
    }

    deletePause(pauseId: number) {
        return this.basicDelete("pauses/" + pauseId);
    }

    //Interactions

    getInteractions(studyId: number, type: string, offset: number = 0, limit: number = 100, orderBy: string = "id", direction: string = "asc") {
        const options: Options = {
            method: "GET",
            path: "interactions",
            headers: {},
            params: { study_id: studyId, type: type, offset: offset, limit: limit, order_by: orderBy, direction: direction },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    getInteractionCount(studyId: number, type: string) {
        const options: Options = {
            method: "GET",
            path: "interactions/count",
            headers: {},
            params: { study_id: studyId, type: type },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    getInteractionCounts(studyId: number) {
        const options: Options = {
            method: "GET",
            path: "interactions/counts",
            headers: {},
            params: { study_id: studyId },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    getInteractionTimeline(studyId: number) {
        const options: Options = {
            method: "GET",
            path: "interactions/timeline",
            headers: {},
            params: { study_id: studyId },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }

    // ── Native questionnaires (Forms) ──────────────────────────────────────
    getFormTemplates() {
        return this.basicRead("forms/templates");
    }
    getStudyForms(studyId: number) {
        return this.basicRead("studies/" + studyId + "/forms");
    }
    getGlobalForms() {
        return this.basicRead("forms/global");
    }
    getGlobalFormsUsage() {
        return this.basicRead("forms/global/usage");
    }
    getForm(formId: number) {
        return this.basicRead("forms/" + formId);
    }
    createForm(form: object) {
        return this.basicCreate("forms", form);
    }
    createFormFromTemplate(templateId: number, studyId: number) {
        const options: Options = {
            method: "POST",
            path: "forms/from-template/" + templateId,
            headers: {},
            params: { study_id: studyId },
            asQuery: true
        };
        this.setHeaders(options);
        return this.request(options);
    }
    updateForm(formId: number, patch: object) {
        return this.basicUpdate("forms/" + formId, patch);
    }
    deleteForm(formId: number) {
        return this.basicDelete("forms/" + formId);
    }
    getStudyFormResponses(studyId: number) {
        return this.basicRead("studies/" + studyId + "/form-responses");
    }
    // Participant-authenticated: returns { form, response } for the renderer.
    getFormForParticipant(formId: number) {
        return this.basicRead("forms/" + formId + "/participant");
    }
    saveFormResponse(formId: number, answers: object, submitted: boolean) {
        const options: Options = {
            method: "PUT",
            path: "forms/" + formId + "/participant",
            headers: {}
        };
        this.setHeaders(options);
        options.body = { answers: answers, submitted: submitted };
        return this.request(options);
    }

    getInteractionExport(studyId: number) {
        const options: Options = {
            method: "GET",
            path: "interactions/export",
            headers: {},
            params: { study_id: studyId },
            asQuery: true,
            responseType: "blob"
        };
        this.setHeaders(options);
        return this.request(options);
    }

    // ---- MAINLY FOR USE IN STUDY FRONTEND ---- //

    //TODO: read condition config

    //Study Frontend related functions

    getStudy(studyId?: number) {
        const options: Options = {
            method: "GET",
            path: "studies/" + (studyId || this.studyId),
        }
        return this.request(options);
    }

    getStudySetupInfo(studyId?: number) {
        return this.basicRead("studies/" + studyId + "/setup-info")
    }

    // Participation related methods

    getParticipant(participantToken: string) {
        const options: Options = {
            method: "GET",
            path: "participants/token/" + participantToken,
        }
        return this.request(options);
    }

    participate(participantToken?: string) {
        let options: Options = {
            method: "GET",
            path: "studies/" + this.studyId + "/participate"
        }
        if (participantToken) {
            options = {
                method: "GET",
                path: "studies/" + this.studyId + "/participate/" + participantToken
            }
        }
        return this.request(options);
    }

    setLoggerKey(loggerKey) {
        this.loggerKey = loggerKey;
    }

    storeTokens(responseJson) {
        localStorage.setItem("tokens", JSON.stringify(responseJson));
    }

    updateAccessToken(responseJson) {
        const tokens = this.readTokens();
        tokens["access_token"] = responseJson["access_token"];
        this.storeTokens(tokens);
    }

    readTokens(key= null) {
        let tokens = localStorage.getItem("tokens");
        if (tokens) {
            tokens = JSON.parse(tokens);
            if (key) {
                return tokens[key];
            }
            return tokens;
        }
        return null;
    }
    
    deleteTokens() {
        localStorage.removeItem("tokens");
    }

    refreshToken() {
        const options: Options = {
            method: "GET",
            path: "participants/refresh",
            headers: {}
        }
        this.setHeaders(options, true);
        return this.request(options);
    }

    me(): Promise<any> {
        const options: Options = {
            method: "GET",
            path: "participants/me",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    getProcedure(procedureId: number): Promise<any> {
        const options: Options = {
            method: "GET",
            path: "procedures/" + procedureId,
            headers: {}
        }
        this.setHeaders(options);
        console.log(options);
        return this.request(options);
    }

    // Helper method to create bulks from interaction lists
    private buildBulkList(interactionList: InteractionTypes, bulkSize: number = 10) {
        const bulks = [];
        while (this[interactionList].length > bulkSize) {
            bulks.push(this[interactionList].splice(0, bulkSize));
        }
        if (this[interactionList].length > 0) {
            bulks.push(this[interactionList].splice(0, this[interactionList].length));
        }
        return bulks;
    }

    // TODO: type callback correctly, starting point could be (conditionId: number, interactions: object[]) => Promise<any>
    logInteractionBulk(path: string, conditionId: number, interactionList: InteractionTypes, bulkSize: number,
                        logInteractionBulkRequest) {
        const interactionBulks = this.buildBulkList(interactionList, bulkSize);
        const interactionBulkRequests = [];
        for (let i = 0; i < interactionBulks.length; i++) {
            interactionBulkRequests.push(logInteractionBulkRequest(path, conditionId, interactionBulks[i]));
        }
        return Promise.allSettled(interactionBulkRequests);
    }

    private logInteractionBulkRequest(path: string, conditionId: number, interactions: object[]) {
        const options: Options = {
            method: "POST",
            path: path,
            headers: {}
        }
        this.setLoggerHeaders(options);
        options.body = {
            condition_id: conditionId,
            interactions: interactions
        };
        return this.request(options);
    }

    private logInteractionRequest(path: string, conditionId: number | null, interaction: object) {
        const options: Options = {
            method: "POST",
            path: path,
            headers: {}
        }
        this.setLoggerHeaders(options);
        options.body = {
            condition_id: conditionId,
            interaction: interaction
        };
        return this.request(options);
    }

    // Mouse Interaction

    logMouseInteraction(conditionId: number, eventType: string, mouseEvent: MouseEvent, timestamp: number,
                           relatedTarget: {} = {}, metaData: {} = {}) {
        const interaction = new MouseInteraction(eventType, timestamp, mouseEvent, relatedTarget, metaData);
        const path = "interaction/mouse";
        return this.logInteractionRequest(path, conditionId, interaction);
    }

    addMouseInteraction(eventType: string, mouseEvent: MouseEvent, timestamp: number, relatedTarget: {} = {}, metaData: {} = {}) {
        this.mouseInteractionList.push(new MouseInteraction(eventType, timestamp, mouseEvent, relatedTarget, metaData));
    }

    logMouseInteractionBulk(conditionId: number, bulkSize: number = 10): Promise<any> {
        const interactionType = "mouseInteractionList"
        const path = "interaction/mouse/bulk";
        return this.logInteractionBulk(path, conditionId, interactionType, bulkSize, this.logInteractionBulkRequest.bind(this));
    }

    // Drag Interaction

    logDragInteraction(conditionId: number, eventType: string, dragEvent: DragEvent, timestamp: number,
                        relatedTarget: {} = {}, metaData: {} = {}) {
        const interaction = new DragInteraction(eventType, timestamp, dragEvent, relatedTarget, metaData);
        const path = "interaction/drag";
        return this.logInteractionRequest(path, conditionId, interaction);
    }

    addDragInteraction(eventType: string, dragEvent: DragEvent, timestamp: number, relatedTarget: {} = {}, metaData: {} = {}) {
        this.dragInteractionList.push(new DragInteraction(eventType, timestamp, dragEvent, relatedTarget, metaData));
    }

    logDragInteractionBulk(conditionId: number, bulkSize: number = 10): Promise<any> {
        const interactionType = "dragInteractionList"
        const path = "interaction/drag/bulk";
        return this.logInteractionBulk(path, conditionId, interactionType, bulkSize, this.logInteractionBulkRequest.bind(this));
    }

    // Keyboard Interaction

    logKeyboardInteraction(conditionId: number, eventType: string, keyboardEvent: KeyboardEvent, timestamp: number,
                           metaData: {} = {}) {
        const interaction = new KeyboardInteraction(eventType, timestamp, keyboardEvent, metaData);
        const path = "interaction/keyboard";
        return this.logInteractionRequest(path, conditionId, interaction);
    }

    addKeyboardInteraction(eventType: string, keyboardEvent: KeyboardEvent, timestamp: number, metaData: {} = {}) {
        this.keyboardInteractionList.push(new KeyboardInteraction(eventType, timestamp, keyboardEvent, metaData));
    }

    logKeyboardInteractionBulk(conditionId: number, bulkSize: number = 10): Promise<any> {
        const interactionType = "keyboardInteractionList"
        const path = "interaction/keyboard/bulk";
        return this.logInteractionBulk(path, conditionId, interactionType, bulkSize, this.logInteractionBulkRequest.bind(this));
    }

    // Touch Interaction

    logTouchInteraction(conditionId: number, eventType: string, touchEvent: TouchEvent, timestamp: number,
                           metaData: {} = {}) {
        const interaction = new TouchInteraction(eventType, timestamp, touchEvent, metaData);
        const path = "interaction/touch";
        return this.logInteractionRequest(path, conditionId, interaction);
    }

    addTouchInteraction(eventType: string, touchEvent: TouchEvent, timestamp: number, metaData: {} = {}) {
        this.touchInteractionList.push(new TouchInteraction(eventType, timestamp, touchEvent, metaData));
    }

    logTouchInteractionBulk(conditionId: number, bulkSize: number = 10): Promise<any> {
        const interactionType = "touchInteractionList"
        const path = "interaction/touch/bulk";
        return this.logInteractionBulk(path, conditionId, interactionType, bulkSize, this.logInteractionBulkRequest.bind(this));
    }

    // Generic Interaction

    logGenericInteraction(conditionId: number, eventType: string, genericEvent: object, timestamp: number,
                        metaData: {} = {}) {
        const interaction = new GenericInteraction(eventType, timestamp, genericEvent, metaData);
        const path = "interaction/generic";
        return this.logInteractionRequest(path, conditionId, interaction);
    }

    addGenericInteraction(eventType: string, genericEvent: object, timestamp: number, metaData: {} = {}) {
        this.genericInteractionList.push(new GenericInteraction(eventType, timestamp, genericEvent, metaData));
    }

    logGenericInteractionBulk(conditionId: number, bulkSize: number = 10): Promise<any> {
        const interactionType = "genericInteractionList"
        const path = "interaction/generic/bulk";
        return this.logInteractionBulk(path, conditionId, interactionType, bulkSize, this.logInteractionBulkRequest.bind(this));
    }

    // Meta Interaction (Same as Generic Interaction but without condition_id
    logMetaInteraction(eventType: string, genericEvent: object, timestamp: number,
                          metaData: {} = {}) {
        const interaction = new GenericInteraction(eventType, timestamp, genericEvent, metaData);
        const path = "interaction/meta";
        return this.logInteractionRequest(path, null, interaction);
    }

    // Procedure related methods

    startProcedure() {
        const options: Options = {
            method: "GET",
            path: "procedures/start",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    nextProcedure() {
        const options: Options = {
            method: "GET",
            path: "procedures/next",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    endProcedure() {
        const options: Options = {
            method: "GET",
            path: "procedures/end",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    currentProcedureStep() {
        const options: Options = {
            method: "GET",
            path: "procedure-steps",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    checkSurveyResult() {
        const options: Options = {
            method: "GET",
            path: "procedures/check-survey-result",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    startNavigator() {
        return new Promise( (resolve, reject) => {
            // get the user token (uuid)
            this.me().then(response => {
                if (response.body) {
                    const participantToken = response.body.token;
                    const url = this.apiUrl + "/" + "procedures/navigator?participant=" + participantToken;
                    this.sse = new EventSource(url, {withCredentials: true});
                    resolve(true);
                }
                reject({
                    text: "Participant not found"
                });
            }).catch(error => {
                console.log(error);
                resolve(error);
            });
        });
    }

    closeNavigator() {
        this.sse.close();
    }

    reconnectNavigator() {
        const options: Options = {
            method: "GET",
            path: "procedures/navigator/reconnect",
            headers: {}
        }
        this.setHeaders(options);
        return this.request(options);
    }

    getNavigator() {
        return this.sse;
    }

    updateNavigator(participantToken: string, source: NavigatorSource, state: NavigatorState, extId: number) {
        const options: Options = {
            method: "POST",
            path: "procedures/navigator",
            headers: {
                "Content-type": "application/json"
            }
        }
        options.body = {
            participant_token: participantToken,
            source: source,
            state: state,
            ext_id: extId
        };
        return this.request(options);
    }

}
export default StudyAlignLib;